import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { ParameterTimeSeriesChartResult } from "@/lib/api";
import {
  resolveChartSeriesLabel,
  resolveTimeSeriesConfigUnit,
  toParameterTimeSeriesChartResultFromModeled,
} from "@/lib/api/parameter-time-series-chart-result.utils";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import { useMapTimeseriesFetchRequests } from "@/components/map/map-control-timeseries-content";

type UseModeledCoordinateTimeseriesChartDataParams = {
  modelId: string;
  latlng: { lat: number; lng: number };
  timeSeriesConfigs: TimeSeriesConfig[];
};

type ModeledCoordinateTimeseriesChartData = {
  /** One entry per parameter that returned data (missing series are simply omitted). */
  chartData: ParameterTimeSeriesChartResult[];
  /** True while the model run or any series query is still loading. */
  pending: boolean;
};

/**
 * Fetches and shapes the modeled time-series a chart needs at a single
 * coordinate: it resolves the parameters referenced by {@link TimeSeriesConfig}s,
 * picks the latest model run, and fires one modeled series query per (parameter,
 * unit) at the given lat/lng.
 *
 * The coordinate mirror of {@link useStationTimeseriesChartData}, so the model-map
 * chart preview drives its graphs from the same primitives as obs-map (and the
 * same modeled endpoint the runtime widget uses on a map click).
 */
export function useModeledCoordinateTimeseriesChartData({
  modelId,
  latlng,
  timeSeriesConfigs,
}: UseModeledCoordinateTimeseriesChartDataParams): ModeledCoordinateTimeseriesChartData {
  const modelRuns = useQuery({
    queryKey: ["parameters", modelId, "model_runs"],
    queryFn: () => unwrapAxiosData(api.parameters.listModelRuns(modelId)),
  });
  const referenceTime = modelRuns.data?.[0]?.reference_time;

  const fetchRequests = useMapTimeseriesFetchRequests(timeSeriesConfigs);

  return useQueries({
    queries: fetchRequests.map(({ parameterId, unitCode }) => ({
      queryKey: [
        "parameters",
        modelId,
        "parameters",
        "timeseries",
        referenceTime,
        latlng.lat,
        latlng.lng,
        parameterId,
        unitCode,
      ] as const,
      queryFn: async () => {
        const response = await api.parameters.getModeledParameterTimeSeriesForCoordinates(
          modelId,
          parameterId,
          referenceTime as string,
          latlng.lat,
          latlng.lng,
          unitCode,
        );
        return toParameterTimeSeriesChartResultFromModeled(response.data, {
          parameterLabel: resolveChartSeriesLabel(parameterId, timeSeriesConfigs),
          unit: resolveTimeSeriesConfigUnit(parameterId, timeSeriesConfigs),
        });
      },
      enabled: !!referenceTime,
      placeholderData: keepPreviousData,
      retry: false,
    })),
    combine: (results) => ({
      chartData: results
        .map((result) => result.data)
        .filter((data): data is ParameterTimeSeriesChartResult => !!data),
      pending: !referenceTime || results.some((result) => result.isPending),
    }),
  });
}
